// useHarok — JEDNA KLÁVESNICA PRACOVNÉHO HÁRKU (ústava 131.4, 131.6; pravidlá V3 časť B). Rozpis prác V3 je prvý používateľ,
// Výmery a Mzdy prídu (131.1 bod 3). Napísané v dávke rozpis_v3 (29. 9. 2026) ako návrh spoločného dielu — preberá hlavná session.
//
// Model: hárok = riadky × stĺpce; jedna os je ČAS (dni), druhá PARTIA/riadok. Šípky idú po obrazovke (Mirko 28. 9.: „obidva kalendáre“):
//   timeAxis 'x' → ←→ deň, ↑↓ partia · timeAxis 'y' → ↑↓ deň, ←→ partia. Kurzor stojí na JEDNEJ z viacerých položiek bunky (lane):
//   pohyb po osi partie najprv prejde položky v bunke, až potom na ďalšiu partiu (Mirko 29. 9.: „kurzor má behať po jednej“).
//   Víkend a sviatok šípky preskočia (isSkipTime), klik ich vyberie. Shift+šípka po osi času označí rozsah dní tej istej partie.
// Klávesy: Tab/Shift+Tab ďalšia/predošlá partia · Home/End prvý/posledný pracovný deň · PgUp/PgDn onPage(±1) · Ctrl+Home onToday ·
//   Enter/F2 onEnter · písanie onType(znak) · Delete/Backspace onDelete · Ctrl+C/X/V onCopy/onCut/onPaste · Alt+N onNew · ? onHelp ·
//   / onSearch · F8 onF8 · Shift+F10 / Ctrl+. onMenu · Esc onEsc. Ctrl+Z / Ctrl+Shift+Z NECHYTÁ — patrí UndoManageru (54.4, 131.6).
// Listener je na window v capture fáze; v textovom poli, textarea, select a contenteditable nerobí nič (v poli platia skratky programu).
import { useEffect, useRef } from 'react'

const inField = (el) => !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)

export function useHarok(cfg) {
  const ref = useRef(cfg)
  ref.current = cfg
  useEffect(() => {
    const h = (e) => {
      const c = ref.current
      if (!c || !c.enabled) return
      if (inField(document.activeElement)) return
      const mod = e.ctrlKey || e.metaKey
      const key = e.key
      const { cur, rows, cols, timeAxis } = c
      const timeKeys = timeAxis === 'x' ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown']
      const partKeys = timeAxis === 'x' ? ['ArrowUp', 'ArrowDown'] : ['ArrowLeft', 'ArrowRight']
      const stop = () => { e.preventDefault(); e.stopPropagation() }
      const lanes = (p, d) => Math.max(1, Number(c.lanesAt ? c.lanesAt(p, d) : 1) || 1)
      const skip = (d) => (c.isSkipTime ? c.isSkipTime(d) : false)
      // pohyb po čase — preskočí víkend a sviatok; na okraji zavolá onPage (týždeň dozadu/dopredu)
      const moveTime = (dir, shift) => {
        let d = cur.d
        do { d += dir } while (d >= 0 && d < cols && skip(d))
        if (d < 0 || d >= cols) { c.onPage && c.onPage(dir, shift); return }
        if (shift) { c.onSelect && c.onSelect(d) } else c.setCur({ p: cur.p, d, l: Math.min(cur.l, lanes(cur.p, d) - 1) })
      }
      const movePart = (dir) => {
        const L = lanes(cur.p, cur.d); const nl = cur.l + dir
        if (nl >= 0 && nl < L && L > 1) { c.setCur({ p: cur.p, d: cur.d, l: nl }); return }
        const np = cur.p + dir; if (np < 0 || np >= rows) return
        const L2 = lanes(np, cur.d)
        c.setCur({ p: np, d: cur.d, l: dir > 0 || L2 <= 1 ? 0 : L2 - 1 })
      }
      if (key === 'Escape') { if (c.onEsc && c.onEsc()) stop(); return }
      if (key === timeKeys[0]) { stop(); return moveTime(-1, e.shiftKey) }
      if (key === timeKeys[1]) { stop(); return moveTime(1, e.shiftKey) }
      if (key === partKeys[0]) { stop(); return movePart(-1) }
      if (key === partKeys[1]) { stop(); return movePart(1) }
      if (key === 'Tab') { stop(); const np = cur.p + (e.shiftKey ? -1 : 1); if (np >= 0 && np < rows) c.setCur({ p: np, d: cur.d, l: 0 }); return }
      if (key === 'Home' && mod) { stop(); return c.onToday && c.onToday() }
      if (key === 'Home' || key === 'End') {
        stop(); let d = key === 'Home' ? 0 : cols - 1; const dir = key === 'Home' ? 1 : -1
        while (d >= 0 && d < cols && skip(d)) d += dir
        if (d >= 0 && d < cols) c.setCur({ p: cur.p, d, l: 0 }); return
      }
      if (key === 'PageUp') { stop(); return c.onPage && c.onPage(-1) }
      if (key === 'PageDown') { stop(); return c.onPage && c.onPage(1) }
      if (key === 'Enter' || key === 'F2') { stop(); return c.onEnter && c.onEnter() }
      if (key === 'Delete' || key === 'Backspace') { stop(); return c.onDelete && c.onDelete() }
      if (key === 'F8') { stop(); return c.onF8 && c.onF8() }
      if (key === 'F10' && e.shiftKey) { stop(); return c.onMenu && c.onMenu() }
      if (mod && key === '.') { stop(); return c.onMenu && c.onMenu() }
      if (mod && !e.altKey && key.toLowerCase() === 'c') { stop(); return c.onCopy && c.onCopy() }
      if (mod && !e.altKey && key.toLowerCase() === 'x') { stop(); return c.onCut && c.onCut() }
      if (mod && !e.altKey && key.toLowerCase() === 'v') { stop(); return c.onPaste && c.onPaste() }
      if (mod && key.toLowerCase() === 'z') return   // UndoManager (54.4)
      if (e.altKey && key.toLowerCase() === 'n') { stop(); return c.onNew && c.onNew() }
      // STAVBA_LOGIKA_V1: Alt+S = Byty a partie stavby pod kurzorom (e.code — na Macu Option+S píše „ß“)
      if (e.altKey && (e.code === 'KeyS' || key.toLowerCase() === 's') && c.onStavba) { stop(); return c.onStavba() }
      if (key === '?') { stop(); return c.onHelp && c.onHelp() }
      if (key === '/') { stop(); return c.onSearch && c.onSearch() }
      if (key.length === 1 && !mod && !e.altKey) { stop(); return c.onType && c.onType(key) }
    }
    window.addEventListener('keydown', h, true)
    return () => window.removeEventListener('keydown', h, true)
  }, [])
}

export default useHarok
